const communicationsHelpEntries = [
  {
    id: 'communications-template-library',
    title: 'Manage communication templates',
    summary:
      'Build and maintain reusable copy for email and portal outreach while keeping merge fields and attachments consistent.',
    audience: 'Accounting and operations teams responsible for client follow-up.',
    keywords: [
      'communications',
      'template library',
      'message template',
      'email template',
      'portal template',
      'merge field',
      'placeholder',
      'personalize',
      'invoice reminder template',
      'late payment reminder',
      'client outreach',
      'follow-up copy',
      'attachment rule',
      'latest invoice pdf',
      'context metadata',
      'communication copy',
    ],
    steps: [
      'Select “Create new template” to compose a new email or portal message from scratch.',
      'Choose an existing template key to revise the subject line, message body, or available merge variables.',
      'Toggle attachment rules to automatically include the latest invoice PDF and optionally supply a filename pattern.',
    ],
    notes: [
      'Template updates take effect immediately across both email delivery and client portal messaging flows.',
    ],
    mergeFieldsLabel: 'Common merge fields',
    mergeFields: [
      'client_name',
      'invoice_number',
      'invoice_total',
      'due_date',
      'po_number',
      'po_remaining_hours',
      'project_name',
      'payment_link',
    ],
    relatedTopics: [
      'communications-templated-email',
      'communications-portal-message',
      'communications-automation-settings',
    ],
  },
  {
    id: 'communications-templated-email',
    title: 'Queue templated email delivery',
    summary:
      'Send personalized reminder emails using merge variables and the asynchronous communication queue.',
    audience: 'Billing specialists sending invoice updates and reminders.',
    keywords: [
      'communications',
      'email',
      'email delivery',
      'email queue',
      'reminder email',
      'payment reminder',
      'follow-up email',
      'notification',
      'outbound message',
      'send email',
      'queued communication',
      'asynchronous delivery',
      'merge variables',
      'invoice email',
      'client follow-up',
    ],
    notes: [
      'Each queued email records a communication log entry with subject, body preview, attachments, and metadata for auditing.',
    ],
    relatedTopics: [
      'communications-template-library',
      'communications-automation-settings',
      'communications-history-log',
    ],
  },
  {
    id: 'communications-portal-message',
    title: 'Log templated portal messages',
    summary:
      'Capture the exact message published through the client portal so every notification is tracked in the audit log.',
    audience: 'Client success and delivery teams sharing updates in the portal.',
    keywords: [
      'communications',
      'portal message',
      'client portal',
      'portal update',
      'portal notification',
      'client announcement',
      'portal follow-up',
      'message log',
      'portal communication',
      'client update',
      'touchpoint',
    ],
    notes: [
      'Portal messages reuse the same template keys and merge variables as email so copy stays synchronized across channels.',
    ],
    relatedTopics: [
      'communications-template-library',
      'communications-history-log',
    ],
  },
  {
    id: 'communications-automation-settings',
    title: 'Configure automation triggers',
    summary:
      'Define when automated reminders should fire based on invoice statuses, due dates, and purchase order thresholds.',
    audience: 'Operations teams planning reminder cadences and compliance workflows.',
    keywords: [
      'communications',
      'automation',
      'reminder automation',
      'auto reminders',
      'follow-up schedule',
      'trigger status',
      'due date reminder',
      'overdue notice',
      'po threshold alert',
      'automated email',
      'auto send',
      'communication workflow',
      'queue automation',
    ],
    notes: [
      'Automation can target draft, sent, viewed, approved, overdue, paid, or void statuses and respects the configured lead or lag days.',
    ],
    relatedTopics: [
      'communications-templated-email',
      'communications-queue-activity',
    ],
  },
  {
    id: 'communications-queue-activity',
    title: 'Review queue activity snapshot',
    summary:
      'Monitor automation status, message counts, and channel mix to confirm clients receive the expected touchpoints.',
    audience: 'Leads verifying delivery performance for specific clients.',
    keywords: [
      'communications',
      'queue',
      'delivery queue',
      'message volume',
      'channel mix',
      'activity snapshot',
      'send summary',
      'automation status',
      'queue status',
      'communication analytics',
      'monitoring',
      'delivery metrics',
      'volume by channel',
    ],
    notes: [
      'Channel badges highlight how many emails versus portal updates have been delivered for the selected client.',
    ],
    relatedTopics: [
      'communications-automation-settings',
      'communications-history-log',
    ],
  },
  {
    id: 'communications-history-log',
    title: 'Audit communication history',
    summary:
      'Access the combined log of every email and portal update, including metadata, context values, invoice references, and timestamps.',
    audience: 'Teams validating client touchpoints or researching disputes.',
    keywords: [
      'communications',
      'history',
      'communication log',
      'audit trail',
      'message history',
      'email history',
      'portal history',
      'client log',
      'outbound log',
      'touchpoint log',
      'delivery history',
      'audit record',
      'communication history',
    ],
    notes: [
      'Context badges expose merge field substitutions while metadata badges display delivery details such as recipient and attachments.',
    ],
    relatedTopics: [
      'communications-templated-email',
      'communications-portal-message',
    ],
  },
];

const communicationsHelpLookup = communicationsHelpEntries.reduce((accumulator, entry) => {
  accumulator[entry.id] = entry;
  return accumulator;
}, {});

Object.freeze(communicationsHelpEntries);
Object.freeze(communicationsHelpLookup);

export { communicationsHelpEntries, communicationsHelpLookup };
export default communicationsHelpEntries;
